'use client';

import { EntityRatingCommentCount, Professional, ProfessionalSchedule } from '@medprev-web-app/api-graphql-client';
import { useEffect, useState } from 'react';

import {
  getProfessionalProfileFromSearchApi,
  ProfessionalProfileFromSearchApi,
} from '@/app/(main)/(pages)/(institucional)/(index)/profissionais/_services/get-professional-profile-from-search-api.service';
import { getLogger } from '@/logger/logger';
import { RatingComment } from '@/types';

export type ProfessionalProfileStatus = 'loading' | 'ready' | 'error';

export interface ProfessionalProfileFromBff {
  professional: Professional;
  commentsCount?: EntityRatingCommentCount | null;
  inPersonData: ProfessionalSchedule[];
  telehealthData: ProfessionalSchedule[];
  initialComments?: RatingComment[];
}

export interface UseProfessionalProfileReturn {
  profile: ProfessionalProfileFromSearchApi | null;
  status: ProfessionalProfileStatus;
}

function fromBff(bff: ProfessionalProfileFromBff): ProfessionalProfileFromSearchApi {
  return {
    professional: bff.professional,
    gender: null,
    commentsCount: bff.commentsCount ?? { total: 0, comments: [] },
    initialComments: bff.initialComments ?? [],
    hasMoreComments: false,
    inPersonSchedules: bff.inPersonData,
    hasTelehealth: bff.telehealthData.length > 0,
  };
}

export function useProfessionalProfile(
  slug: string,
  usesNewSchedule: boolean,
  bff: ProfessionalProfileFromBff,
): UseProfessionalProfileReturn {
  const [profile, setProfile] = useState<ProfessionalProfileFromSearchApi | null>(null);
  const [status, setStatus] = useState<ProfessionalProfileStatus>('loading');

  useEffect(() => {
    if (!usesNewSchedule) {
      return;
    }

    let cancelled = false;

    setProfile(null);
    setStatus('loading');

    getProfessionalProfileFromSearchApi(slug)
      .then((result) => {
        if (cancelled) {
          return;
        }

        setProfile(result);
        setStatus('ready');
      })
      .catch((error) => {
        getLogger('UseProfessionalProfile').error('Error fetching the professional profile', { slug, error });

        if (cancelled) {
          return;
        }

        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [slug, usesNewSchedule]);

  if (!usesNewSchedule) {
    return { profile: fromBff(bff), status: 'ready' };
  }

  return { profile, status };
}
